'use client';

import { Search } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import ShiftDigitalEventName from '~/constants/dealership/ShiftDigitalEventName';
import { absoluteBaseUrl } from '~/constants/environment';
import { useAnalyticsSection } from '~/context/common/SectionAnalyticsContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import buildDealershipUrl from '~/utilities/dealership/buildDealershipUrl';
import { NavLink } from './PageHeader.styles';

const navTextMap = {
  search: 'Search cars',
  sell: 'Sell/Trade',
  service: 'Service'
};

type ActivePage = 'search' | 'service' | null;

type OwnProps = {
  isMobile?: boolean;
};

export default function Links({ isMobile }: OwnProps) {
  const pathname = usePathname();
  const section = useAnalyticsSection();
  const dealershipId = useDealershipId();

  const activePage = useMemo((): ActivePage => {
    if (pathname?.includes('/cars')) return 'search';
    if (pathname?.includes('/service') || pathname?.includes('/schedule'))
      return 'service';

    return null;
  }, [pathname]);

  return (
    <nav
      className={
        isMobile
          ? 'flex h-7xl items-center justify-evenly gap-md sm:hidden'
          : 'hidden gap-sm sm:flex'
      }
    >
      <NavLink
        href="/cars"
        selected={activePage === 'search'}
        data-adobe-id="search-cars"
        data-analytics-id={section}
        data-analytics-attributes={{
          item: navTextMap.search,
          section
        }}
        data-testid="search-cars"
      >
        <Search size={16} />
        {isMobile ? 'Search' : navTextMap.search}
      </NavLink>

      <NavLink
        href={buildDealershipUrl(
          `${absoluteBaseUrl}/sell-my-car`,
          dealershipId,
          'stc'
        )}
        target="_blank"
        data-adobe-id="sell-trade"
        data-analytics-id={section}
        data-analytics-attributes={{
          item: navTextMap.sell,
          section
        }}
        data-testid="sell-trade"
      >
        {navTextMap.sell}
      </NavLink>

      <NavLink
        href="/service"
        selected={activePage === 'service'}
        data-adobe-id="service"
        data-analytics-id={section}
        data-analytics-attributes={{
          item: navTextMap.service,
          section
        }}
        data-sda-event={ShiftDigitalEventName.ScheduleServiceClick}
        data-testid="service"
      >
        {navTextMap.service}
      </NavLink>
    </nav>
  );
}
